// Corporate Catalog Builder — V2.
// Standalone module separate from regular CatalogBuilderPage (do not mix flows).
// Provides:
//   - cover / branding / language / currency / show_prices / show_mrp
//   - Cover Image upload + Back Cover contact details
//   - Advanced product filters (brand/category/subcategory/deity/region/occasion/status/MRP range/search)
//   - Add Products by Code (comma/newline/semicolon/pipe separators)
//   - Drag-handle reorder for sections + products (chevron-based)
//   - MOQ pricing editor per product (direct OR % off MRP) + Delivery Days
//   - Client metadata (company/contact/validity/delivery/GST/default-terms)
//   - Preview / Flipbook / PDF / Save / Share buttons
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getImageUrl, formatApiError } from '../lib/api';
import { downloadPdfWithCache } from '../lib/pdf';
import { devError } from '../lib/logger';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, Save, Search, Loader2, Building2, Pencil, Check, ChevronUp, ChevronDown, Tag, Download, BookOpen, ExternalLink, Trash2, Upload, Filter, RotateCcw, ImageIcon } from 'lucide-react';

const CURRENCIES = [
  { code: 'INR', symbol: '\u20B9', label: 'INR \u20B9 (default)' },
  { code: 'USD', symbol: '$', label: 'USD $' },
  { code: 'GBP', symbol: '\u00a3', label: 'GBP \u00a3' },
  { code: 'EUR', symbol: '\u20ac', label: 'EUR \u20ac' },
  { code: 'CAD', symbol: '$', label: 'CAD $' },
  { code: 'AUD', symbol: 'A$', label: 'AUD A$' },
  { code: 'AED', symbol: '\u062f.\u0625', label: 'AED \u062f.\u0625' },
];

function L({ children }) { return <Label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] mb-2 block">{children}</Label>; }

// ----- MOQ editor dialog --------------------------------------------------
function MoqEditorDialog({ open, onClose, product, mrp, currencySymbol, tiers, onSave }) {
  // Each editor row carries a stable `_rid` so React preserves input state correctly
  // across reorders / inserts / deletes — without `_rid`, using the array index as
  // key causes the wrong input to receive focus / state after a row is moved.
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!open) return;
    setRows((tiers || []).map((t, i) => ({ _rid: `rid-${Date.now()}-${i}`, ...t })));
  }, [open, tiers]);
  if (!open || !product) return null;

  const addRow = () => setRows(rs => [...rs, { _rid: `rid-new-${Date.now()}-${rs.length}`, moq_quantity: '', pricing_method: 'direct', direct_price: '', discount_percent: '', delivery_days: '', remarks: '' }]);
  const upd = (i, key, value) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [key]: value } : r));
  const rm = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));
  const move = (i, dir) => setRows(rs => {
    const ni = i + dir; if (ni < 0 || ni >= rs.length) return rs;
    const next = [...rs]; [next[i], next[ni]] = [next[ni], next[i]]; return next;
  });

  const calcPreview = (row) => {
    const m = parseFloat(row.mrp_ref || mrp || 0);
    if (row.pricing_method === 'discount_percent') {
      const p = parseFloat(row.discount_percent || 0);
      if (isNaN(p) || p < 0 || p > 100) return '—';
      return `${currencySymbol}${Math.round(m * (1 - p/100)).toLocaleString()}`;
    }
    const d = parseFloat(row.direct_price || 0);
    if (isNaN(d) || d < 0) return '—';
    return `${currencySymbol}${Math.round(d).toLocaleString()}`;
  };

  const save = () => {
    const cleaned = [];
    const seen = new Set();
    for (const r of rows) {
      const q = parseInt(r.moq_quantity, 10);
      if (!q || q <= 0) { toast.error(`MOQ quantity must be > 0 (got "${r.moq_quantity}")`); return; }
      if (seen.has(q)) { toast.error(`Duplicate MOQ quantity: ${q}`); return; }
      seen.add(q);
      // delivery_days is optional; only parse if non-blank
      let deliveryDays = null;
      const rawD = (r.delivery_days ?? '').toString().trim();
      if (rawD) {
        const d = parseInt(rawD, 10);
        if (!d || d <= 0) { toast.error(`Delivery days must be > 0 (got "${rawD}")`); return; }
        deliveryDays = d;
      }
      const base = { moq_quantity: q, delivery_days: deliveryDays, remarks: (r.remarks || '').trim() };
      if (r.pricing_method === 'discount_percent') {
        const p = parseFloat(r.discount_percent);
        if (isNaN(p) || p < 0 || p > 100) { toast.error(`Discount % must be 0-100 (got "${r.discount_percent}")`); return; }
        cleaned.push({ ...base, pricing_method: 'discount_percent', discount_percent: p, direct_price: null });
      } else {
        const d = parseFloat(r.direct_price);
        if (isNaN(d) || d < 0) { toast.error(`Direct price must be a positive number (got "${r.direct_price}")`); return; }
        cleaned.push({ ...base, pricing_method: 'direct', direct_price: d, discount_percent: null });
      }
    }
    cleaned.sort((a, b) => a.moq_quantity - b.moq_quantity);
    onSave(cleaned);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white rounded-sm border-[#E5E0D8] max-w-3xl" data-testid="moq-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-lg">MOQ Pricing — {product.product_code} <span className="text-sm text-[#5C5C5C] ml-2 font-normal">{product.product_name}</span></DialogTitle>
        </DialogHeader>
        <div className="text-xs text-[#5C5C5C] -mt-2">Current MRP: <span className="font-medium text-[#1A1A1A]">{currencySymbol}{Math.round(mrp || 0).toLocaleString()}</span>. Discount-% tiers recalculate when the Product Master MRP changes. Direct-price tiers stay fixed at the value you enter (in the catalog currency). Delivery days is optional and shown to the customer.</div>
        <div className="space-y-3 py-3 max-h-[55vh] overflow-y-auto">
          {rows.length === 0 ? (
            <p className="text-sm text-center text-[#5C5C5C] py-8 border border-dashed border-[#E5E0D8] rounded-sm">No MOQ tiers yet. Click &ldquo;Add Tier&rdquo; to start.</p>
          ) : rows.map((r, i) => (
            <div key={r._rid} className="grid grid-cols-[50px_1fr_130px_130px_110px_1fr_44px] gap-2 items-end p-3 bg-[#FDFBF7] border border-[#E5E0D8] rounded-sm" data-testid={`moq-row-${i}`}>
              <div>
                <L>Order</L>
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} disabled={i===0} className="p-1 rounded border border-[#E5E0D8] disabled:opacity-30"><ChevronUp size={11}/></button>
                  <button onClick={() => move(i, 1)} disabled={i===rows.length-1} className="p-1 rounded border border-[#E5E0D8] disabled:opacity-30"><ChevronDown size={11}/></button>
                </div>
              </div>
              <div>
                <L>MOQ Qty *</L>
                <Input type="number" min="1" value={r.moq_quantity ?? ''} onChange={e => upd(i, 'moq_quantity', e.target.value)} className="rounded-sm border-[#E5E0D8] h-9 text-sm" data-testid={`moq-qty-${i}`} placeholder="100" />
              </div>
              <div>
                <L>Method</L>
                <Select value={r.pricing_method || 'direct'} onValueChange={v => upd(i, 'pricing_method', v)}>
                  <SelectTrigger className="rounded-sm border-[#E5E0D8] h-9" data-testid={`moq-method-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct Price</SelectItem>
                    <SelectItem value="discount_percent">% off MRP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <L>{r.pricing_method === 'discount_percent' ? 'Discount %' : `Price (${currencySymbol})`}</L>
                {r.pricing_method === 'discount_percent' ? (
                  <Input type="number" min="0" max="100" step="0.5" value={r.discount_percent ?? ''} onChange={e => upd(i, 'discount_percent', e.target.value)} className="rounded-sm border-[#E5E0D8] h-9 text-sm" data-testid={`moq-discount-${i}`} placeholder="20" />
                ) : (
                  <Input type="number" min="0" step="1" value={r.direct_price ?? ''} onChange={e => upd(i, 'direct_price', e.target.value)} className="rounded-sm border-[#E5E0D8] h-9 text-sm" data-testid={`moq-price-${i}`} placeholder="850" />
                )}
                <p className="text-[10px] mt-1 text-[#888]">= <span className="text-[#C5A059] font-medium">{calcPreview(r)}</span></p>
              </div>
              <div>
                <L>Delivery Days</L>
                <Input type="number" min="1" value={r.delivery_days ?? ''} onChange={e => upd(i, 'delivery_days', e.target.value)} className="rounded-sm border-[#E5E0D8] h-9 text-sm" data-testid={`moq-delivery-${i}`} placeholder="30" />
              </div>
              <div>
                <L>Remarks (internal)</L>
                <Input value={r.remarks || ''} onChange={e => upd(i, 'remarks', e.target.value)} className="rounded-sm border-[#E5E0D8] h-9 text-sm" placeholder="optional" maxLength={120} />
              </div>
              <button onClick={() => rm(i)} className="text-[#9E2A2B] hover:bg-red-50 p-2 rounded mb-1" title="Remove tier" data-testid={`moq-remove-${i}`}><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={addRow} className="rounded-sm border-[#C5A059] text-[#C5A059] hover:bg-[#F2E8D5]" data-testid="moq-add-tier-btn"><Plus size={14} className="mr-1"/>Add Tier</Button>
          <Button variant="outline" onClick={onClose} className="rounded-sm border-[#E5E0D8]">Cancel</Button>
          <Button onClick={save} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="moq-save-btn"><Check size={14} className="mr-1"/>Save Tiers</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----- Main builder page --------------------------------------------------
export default function CorporateCatalogBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [meta, setMeta] = useState({ title: '', subtitle: '', intro_note: '', cover_image: '', footer_contact: '' });
  const [branding] = useState({ primary_color: '#C5A059', company_name: 'AARYA24kt', tagline: '', logo_url: '' });
  const [language, setLanguage] = useState('en');
  const [currency, setCurrency] = useState({ code: 'INR', symbol: '\u20B9', exchange_rate_to_inr: null, rounding_mode: 'nearest_1' });
  const [showPrices, setShowPrices] = useState(true);
  const [showMrp, setShowMrp] = useState(true);
  const [corporateMeta, setCorporateMeta] = useState({ client_company: '', contact_person: '', validity_date: '', delivery_terms: '', gst_note: '', default_terms: 'Prices are indicative and subject to confirmation at the time of order.', custom_note: '' });
  const [backCover, setBackCover] = useState({ name: '', phone: '', email: '', website: '', address: '' });
  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [newSectionName, setNewSectionName] = useState('');
  const [renamingSectionId, setRenamingSectionId] = useState(null);
  const [renamingValue, setRenamingValue] = useState('');
  const [corporatePricing, setCorporatePricing] = useState({});  // { product_id: [tier, ...] }
  const [browseProducts, setBrowseProducts] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ brand: [], category: [], subcategory: [], deity: [], region: [], occasion: [] });
  const [filters, setFilters] = useState({ search: '', brand: '', category: '', subcategory: '', deity: '', region: '', occasion: '', status: '', mrp_min: '', mrp_max: '' });
  const [showAdvFilters, setShowAdvFilters] = useState(false);
  const [codesInput, setCodesInput] = useState('');
  const [codesLoading, setCodesLoading] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [moqTarget, setMoqTarget] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [prewarmBusy, setPrewarmBusy] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);

  // Taxonomy load (filters) — same shape as regular CatalogBuilderPage.
  const fetchTaxonomy = useCallback(async () => {
    try {
      const types = ['brand', 'category', 'subcategory', 'deity', 'region', 'occasion'];
      const results = await Promise.all(types.map(t => api.get(`/taxonomy?type=${t}`)));
      const tax = {}; types.forEach((t, i) => { tax[t] = results[i].data; });
      setTaxonomy(tax);
    } catch (err) { devError('Taxonomy load failed', err); }
  }, []);

  // Server-side filtered product search — supports 800+ products without UI lag.
  const searchProducts = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (filters.search) params.search = filters.search;
      if (filters.brand) params.brand = filters.brand;
      if (filters.category) params.category = filters.category;
      if (filters.subcategory) params.subcategory = filters.subcategory;
      if (filters.deity) params.deity = filters.deity;
      if (filters.region) params.region = filters.region;
      if (filters.occasion) params.occasion = filters.occasion;
      if (filters.status) params.status = filters.status;
      if (filters.mrp_min) params.mrp_min = parseFloat(filters.mrp_min);
      if (filters.mrp_max) params.mrp_max = parseFloat(filters.mrp_max);
      const { data } = await api.get('/products', { params });
      setBrowseProducts(data.products || []);
    } catch (err) { devError('Product search failed', err); }
  }, [filters]);

  useEffect(() => { fetchTaxonomy(); }, [fetchTaxonomy]);
  useEffect(() => { searchProducts(); }, [searchProducts]);

  useEffect(() => { if (!isEdit) return; (async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/catalogs/${id}`);
      if (data.catalog_type !== 'corporate') { toast.error('This is not a corporate catalog'); navigate('/corporate-catalogs'); return; }
      setMeta({ title: data.title || '', subtitle: data.subtitle || '', intro_note: data.intro_note || '', cover_image: data.cover_image || '', footer_contact: data.footer_contact || '' });
      setLanguage(data.language || 'en');
      setCurrency(data.currency || { code: 'INR', symbol: '\u20B9', exchange_rate_to_inr: null, rounding_mode: 'nearest_1' });
      setShowPrices(data.show_prices !== false);
      setShowMrp(data.show_mrp !== false);
      setCorporateMeta(prev => ({ ...prev, ...(data.corporate_meta || {}) }));
      setBackCover(prev => ({ ...prev, ...(data.back_cover || {}) }));
      setSections(data.sections || []);
      setActiveSection(data.sections?.[0]?.id || null);
      setCorporatePricing(data.corporate_pricing || {});
      setShareToken(data.share_token || null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Failed to load'); devError('load catalog', err); }
    setLoading(false);
  })(); }, [id, isEdit, navigate]);

  const updateFilter = (k, v) => setFilters(prev => ({ ...prev, [k]: v === 'all' ? '' : v }));
  const clearFilters = () => setFilters({ search: '', brand: '', category: '', subcategory: '', deity: '', region: '', occasion: '', status: '', mrp_min: '', mrp_max: '' });

  const inCatalogIds = new Set(sections.flatMap(s => (s.products || []).map(p => p.id)));
  const filteredProducts = browseProducts.filter(p => !inCatalogIds.has(p.id));

  // ----- sections -----
  const addSection = () => {
    if (!newSectionName.trim()) return;
    const sec = { id: `section-${Date.now()}`, name: newSectionName.trim(), products: [] };
    setSections(s => [...s, sec]); setActiveSection(sec.id); setNewSectionName('');
  };
  const removeSection = (sid) => {
    setSections(prev => prev.filter(s => s.id !== sid));
    if (activeSection === sid) setActiveSection(sections[0]?.id || null);
  };
  const moveSection = (i, dir) => {
    setSections(prev => {
      const ni = i + dir; if (ni < 0 || ni >= prev.length) return prev;
      const next = [...prev]; [next[i], next[ni]] = [next[ni], next[i]]; return next;
    });
  };
  const startRename = (s) => { setRenamingSectionId(s.id); setRenamingValue(s.name); };
  const commitRename = () => {
    const v = renamingValue.trim();
    if (!v) { toast.error('Section name cannot be empty'); return; }
    setSections(prev => prev.map(s => s.id === renamingSectionId ? { ...s, name: v } : s));
    setRenamingSectionId(null); setRenamingValue('');
  };

  // ----- products -----
  const addProductToSection = (product) => {
    if (!activeSection) { toast.error('Create or select a section first'); return; }
    setSections(prev => prev.map(s => s.id !== activeSection ? s : (s.products?.some(p => p.id === product.id) ? s : { ...s, products: [...(s.products || []), product] })));
  };
  const removeProductFromSection = (sid, pid) => {
    setSections(prev => prev.map(s => s.id === sid ? { ...s, products: s.products.filter(p => p.id !== pid) } : s));
    setCorporatePricing(prev => { const next = { ...prev }; delete next[pid]; return next; });
  };
  const moveProductInSection = (sid, i, dir) => {
    setSections(prev => prev.map(s => {
      if (s.id !== sid) return s;
      const ni = i + dir; if (ni < 0 || ni >= s.products.length) return s;
      const next = [...s.products]; [next[i], next[ni]] = [next[ni], next[i]]; return { ...s, products: next };
    }));
  };

  // ----- Add Products by Code -----
  const handleAddByCodes = async () => {
    if (!activeSection) { toast.error('Select or create a section first'); return; }
    const raw = codesInput.trim();
    if (!raw) { toast.error('Enter one or more product codes'); return; }
    setCodesLoading(true);
    try {
      const { data } = await api.post('/products/resolve-codes', { codes: raw });
      const { found = [], missing = [], requested = 0 } = data;
      if (found.length) {
        setSections(prev => prev.map(s => {
          if (s.id !== activeSection) return s;
          const existing = new Set(s.products.map(p => p.id));
          const toAdd = found.filter(p => !existing.has(p.id));
          return { ...s, products: [...s.products, ...toAdd] };
        }));
      }
      if (found.length && missing.length) toast.success(`Added ${found.length} of ${requested}. Missing: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? '…' : ''}`);
      else if (found.length) toast.success(`Added ${found.length} product${found.length === 1 ? '' : 's'}`);
      else toast.error(`No products matched. Missing: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? '…' : ''}`);
      setCodesInput('');
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Failed to resolve codes'); devError('resolve-codes', err); }
    setCodesLoading(false);
  };

  // ----- Cover image upload -----
  const handleCoverUpload = async (file) => {
    if (!file) return;
    setCoverUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/upload', fd);
      setMeta(p => ({ ...p, cover_image: data.url }));
      toast.success('Cover image uploaded');
    } catch (err) { toast.error('Cover upload failed'); devError('cover upload', err); }
    setCoverUploading(false);
  };

  // ----- Save -----
  const handleSave = async () => {
    if (!meta.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    const sectionsPayload = sections.map(s => ({ id: s.id, name: s.name, product_ids: (s.products || []).map(p => p.id) }));
    const body = {
      title: meta.title.trim(), subtitle: meta.subtitle, intro_note: meta.intro_note, cover_image: meta.cover_image, footer_contact: meta.footer_contact,
      status: 'draft', sections: sectionsPayload, branding, language, back_cover: backCover, show_prices: showPrices,
      currency, catalog_type: 'corporate', corporate_pricing: corporatePricing, corporate_meta: corporateMeta, show_mrp: showMrp,
    };
    try {
      const { data } = isEdit ? await api.put(`/corporate-catalogs/${id}`, body) : await api.post('/corporate-catalogs', body);
      toast.success(isEdit ? 'Saved' : 'Created');
      if (!isEdit) navigate(`/corporate-catalogs/${data.id}/edit`);
      else { setSections(data.sections || []); setCorporatePricing(data.corporate_pricing || {}); setShareToken(data.share_token || shareToken); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Save failed'); }
    setSaving(false);
  };

  const downloadPdf = async () => {
    if (!id) { toast.error('Save the catalog first'); return; }
    setPdfBusy(true);
    await downloadPdfWithCache(`/catalogs/${id}/pdf`, meta.title || 'corporate-catalog');
    setPdfBusy(false);
  };

  const preparePdf = async () => {
    if (!id) { toast.error('Save the catalog first'); return; }
    setPrewarmBusy(true);
    try {
      await api.post(`/catalogs/${id}/prewarm`);
      toast.success('PDF is being prepared in the background. You can download it in ~30s.');
    } catch (err) { toast.error('Failed to start PDF generation'); devError('prewarm', err); }
    setPrewarmBusy(false);
  };

  const openSharedLink = () => {
    if (!shareToken) { toast.error('Save the catalog first'); return; }
    window.open(`/shared/${shareToken}`, '_blank', 'noopener,noreferrer');
  };
  const openFlipbook = () => {
    if (!shareToken) { toast.error('Save the catalog first'); return; }
    window.open(`/flipbook/${shareToken}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) return <div className="text-center py-16 text-sm text-[#5C5C5C]"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>;

  const moqTargetTiers = moqTarget ? (corporatePricing[moqTarget.id] || []) : [];
  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div data-testid="corporate-catalog-builder-page" className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/corporate-catalogs')} className="rounded-sm"><ArrowLeft size={16} className="mr-1" />Back</Button>
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#C5A059] font-semibold">Corporate Catalog</p>
            <h2 className="font-serif text-2xl text-[#1A1A1A]">{isEdit ? 'Edit' : 'New'} Corporate Catalog</h2>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isEdit && shareToken && (
            <>
              <Button variant="outline" onClick={openSharedLink} className="rounded-sm border-[#E5E0D8] text-sm" data-testid="open-corp-shared-btn"><ExternalLink size={14} className="mr-1.5"/>Preview</Button>
              <Button variant="outline" onClick={openFlipbook} className="rounded-sm border-[#E5E0D8] text-sm" data-testid="open-corp-flipbook-btn"><BookOpen size={14} className="mr-1.5"/>Flipbook</Button>
              <Button variant="outline" onClick={preparePdf} disabled={prewarmBusy} className="rounded-sm border-[#C5A059] text-[#C5A059] hover:bg-[#F2E8D5] text-sm" data-testid="prewarm-corp-pdf-btn">{prewarmBusy ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Upload size={14} className="mr-1.5"/>}Prepare PDF</Button>
              <Button variant="outline" onClick={downloadPdf} disabled={pdfBusy} className="rounded-sm border-[#E5E0D8] text-sm" data-testid="export-corp-pdf-btn">{pdfBusy ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Download size={14} className="mr-1.5"/>}PDF</Button>
            </>
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="save-corporate-catalog-btn"><Save size={16} className="mr-2" />{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>

      {/* ----- Top metadata grid ----- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E5E0D8] rounded-sm p-5 space-y-4">
          <h3 className="font-serif text-lg text-[#1A1A1A]">Catalog Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><L>Title *</L><Input data-testid="corp-title" value={meta.title} onChange={e => setMeta(m => ({...m, title: e.target.value}))} className="rounded-sm border-[#E5E0D8]" /></div>
            <div><L>Subtitle</L><Input data-testid="corp-subtitle" value={meta.subtitle} onChange={e => setMeta(m => ({...m, subtitle: e.target.value}))} className="rounded-sm border-[#E5E0D8]" /></div>
          </div>
          <div><L>Intro Note</L><Textarea data-testid="corp-intro" value={meta.intro_note} onChange={e => setMeta(m => ({...m, intro_note: e.target.value}))} rows={2} className="rounded-sm border-[#E5E0D8]" /></div>

          {/* Cover image upload */}
          <div>
            <L>Cover Image</L>
            <div className="flex items-center gap-3">
              {meta.cover_image ? (
                <div className="relative group">
                  <img src={getImageUrl(meta.cover_image)} alt="Cover" className="w-24 h-24 object-cover rounded-sm border border-[#E5E0D8]" data-testid="corp-cover-preview" />
                  <button type="button" onClick={() => setMeta(p => ({ ...p, cover_image: '' }))} className="absolute -top-2 -right-2 w-5 h-5 bg-[#9E2A2B] text-white rounded-full flex items-center justify-center" data-testid="corp-cover-remove"><X size={11} /></button>
                </div>
              ) : (
                <label className="w-24 h-24 border border-dashed border-[#E5E0D8] rounded-sm flex flex-col items-center justify-center cursor-pointer hover:bg-[#FDFBF7]" data-testid="corp-cover-upload-label">
                  {coverUploading ? <Loader2 size={18} className="text-[#C5A059] animate-spin" /> : <><ImageIcon size={18} className="text-[#C5A059]"/><span className="text-[9px] text-[#5C5C5C] mt-1">Upload</span></>}
                  <input type="file" accept="image/*" className="hidden" onChange={e => handleCoverUpload(e.target.files?.[0])} data-testid="corp-cover-upload-input" />
                </label>
              )}
              <Input value={meta.cover_image} onChange={e => setMeta(p => ({ ...p, cover_image: e.target.value }))} placeholder="Or paste image URL (https://…)" className="rounded-sm border-[#E5E0D8] text-xs flex-1" data-testid="corp-cover-url" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><L>Language</L>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="rounded-sm border-[#E5E0D8]" data-testid="corp-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="hi">Hindi</SelectItem>
                  <SelectItem value="ta">Tamil</SelectItem>
                  <SelectItem value="te">Telugu</SelectItem>
                  <SelectItem value="bn">Bengali</SelectItem>
                  <SelectItem value="mr">Marathi</SelectItem>
                  <SelectItem value="gu">Gujarati</SelectItem>
                  <SelectItem value="pa">Punjabi</SelectItem>
                  <SelectItem value="kn">Kannada</SelectItem>
                  <SelectItem value="ml">Malayalam</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><L>Currency</L>
              <Select value={currency.code} onValueChange={code => { const c = CURRENCIES.find(x => x.code === code); setCurrency(prev => ({ ...prev, code, symbol: c?.symbol || prev.symbol })); }}>
                <SelectTrigger className="rounded-sm border-[#E5E0D8]" data-testid="corp-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {currency.code !== 'INR' && (
              <div><L>1 {currency.code} = ? INR</L>
                <Input data-testid="corp-exchange-rate" type="number" min="0" step="0.01" value={currency.exchange_rate_to_inr ?? ''} onChange={e => setCurrency(p => ({ ...p, exchange_rate_to_inr: parseFloat(e.target.value) || null }))} placeholder="83.00" className="rounded-sm border-[#E5E0D8]" />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <label className="flex items-center gap-2 text-xs text-[#1A1A1A]"><input type="checkbox" checked={showPrices} onChange={e => setShowPrices(e.target.checked)} data-testid="corp-show-prices" />Show prices on this catalog</label>
            <label className="flex items-center gap-2 text-xs text-[#1A1A1A]"><input type="checkbox" disabled={!showPrices} checked={showMrp} onChange={e => setShowMrp(e.target.checked)} data-testid="corp-show-mrp" />Show MRP alongside MOQ pricing</label>
          </div>
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-sm p-5 space-y-3">
          <h3 className="font-serif text-lg text-[#1A1A1A] flex items-center gap-2"><Building2 size={16} className="text-[#C5A059]"/>Client & Corporate Details</h3>
          <div><L>Client / Company</L><Input data-testid="corp-client" value={corporateMeta.client_company} onChange={e => setCorporateMeta(m => ({...m, client_company: e.target.value}))} className="rounded-sm border-[#E5E0D8]" placeholder="Acme Corp" /></div>
          <div><L>Contact Person</L><Input data-testid="corp-contact" value={corporateMeta.contact_person} onChange={e => setCorporateMeta(m => ({...m, contact_person: e.target.value}))} className="rounded-sm border-[#E5E0D8]" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><L>Validity Date</L><Input type="date" data-testid="corp-validity" value={corporateMeta.validity_date} onChange={e => setCorporateMeta(m => ({...m, validity_date: e.target.value}))} className="rounded-sm border-[#E5E0D8]" /></div>
            <div><L>Delivery Terms</L><Input value={corporateMeta.delivery_terms} onChange={e => setCorporateMeta(m => ({...m, delivery_terms: e.target.value}))} className="rounded-sm border-[#E5E0D8]" placeholder="FOB Mumbai" /></div>
          </div>
          <div><L>GST / Tax Note</L><Input value={corporateMeta.gst_note} onChange={e => setCorporateMeta(m => ({...m, gst_note: e.target.value}))} className="rounded-sm border-[#E5E0D8]" placeholder="Plus GST as applicable" /></div>
          <div><L>Default Terms</L><Textarea rows={2} data-testid="corp-default-terms" value={corporateMeta.default_terms} onChange={e => setCorporateMeta(m => ({...m, default_terms: e.target.value}))} className="rounded-sm border-[#E5E0D8] text-xs" /></div>
          <div><L>Custom Note</L><Textarea rows={2} value={corporateMeta.custom_note || ''} onChange={e => setCorporateMeta(m => ({...m, custom_note: e.target.value}))} className="rounded-sm border-[#E5E0D8] text-xs" placeholder="Highlight at top of terms page" /></div>
        </div>
      </div>

      {/* ----- Back cover contact card ----- */}
      <div className="bg-white border border-[#E5E0D8] rounded-sm p-5 space-y-3">
        <h3 className="font-serif text-lg text-[#1A1A1A]">Back Cover Contact Details</h3>
        <p className="text-xs text-[#5C5C5C] -mt-2">Shown on the final page of the PDF, shared link, mobile view, and flipbook.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div><L>Contact Name</L><Input data-testid="corp-back-name" value={backCover.name} onChange={e => setBackCover(b => ({...b, name: e.target.value}))} className="rounded-sm border-[#E5E0D8]" placeholder="AARYA24kt Sales Desk" /></div>
          <div><L>Mobile / Phone</L><Input data-testid="corp-back-phone" value={backCover.phone} onChange={e => setBackCover(b => ({...b, phone: e.target.value}))} className="rounded-sm border-[#E5E0D8]" placeholder="+91 98xxx xxxxx" /></div>
          <div><L>Email</L><Input data-testid="corp-back-email" value={backCover.email} onChange={e => setBackCover(b => ({...b, email: e.target.value}))} className="rounded-sm border-[#E5E0D8]" placeholder="sales@aarya24kt.com" /></div>
          <div><L>Website</L><Input data-testid="corp-back-website" value={backCover.website} onChange={e => setBackCover(b => ({...b, website: e.target.value}))} className="rounded-sm border-[#E5E0D8]" placeholder="www.aarya24kt.com" /></div>
          <div className="md:col-span-2"><L>Address</L><Input data-testid="corp-back-address" value={backCover.address} onChange={e => setBackCover(b => ({...b, address: e.target.value}))} className="rounded-sm border-[#E5E0D8]" placeholder="Mumbai, India" /></div>
        </div>
      </div>

      {/* ----- Sections + product picker ----- */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Input placeholder="New section name…" value={newSectionName} onChange={e => setNewSectionName(e.target.value)} onKeyDown={e => e.key==='Enter' && addSection()} className="rounded-sm border-[#E5E0D8] max-w-xs" data-testid="corp-section-input" />
            <Button onClick={addSection} disabled={!newSectionName.trim()} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="corp-add-section-btn"><Plus size={14} className="mr-1"/>Section</Button>
          </div>

          {sections.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E5E0D8] rounded-sm p-12 text-center text-sm text-[#5C5C5C]">Add a section to start organising products.</div>
          ) : sections.map((section, sIdx) => (
            <div key={section.id} className={`bg-white border rounded-sm p-4 cursor-pointer ${activeSection===section.id ? 'border-[#C5A059]' : 'border-[#E5E0D8]'}`} onClick={() => setActiveSection(section.id)} data-testid={`corp-section-${section.id}`}>
              <div className="flex items-center justify-between mb-3 gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <button onClick={e => { e.stopPropagation(); moveSection(sIdx, -1); }} disabled={sIdx===0} className="p-1 text-[#C5A059] disabled:opacity-30"><ChevronUp size={14}/></button>
                  <button onClick={e => { e.stopPropagation(); moveSection(sIdx, 1); }} disabled={sIdx===sections.length-1} className="p-1 text-[#C5A059] disabled:opacity-30"><ChevronDown size={14}/></button>
                  {renamingSectionId === section.id ? (
                    <div className="flex items-center gap-2 flex-1" onClick={e => e.stopPropagation()}>
                      <Input autoFocus value={renamingValue} onChange={e => setRenamingValue(e.target.value)} onKeyDown={e => { if (e.key==='Enter') commitRename(); if (e.key==='Escape') setRenamingSectionId(null); }} className="h-7 text-sm rounded-sm border-[#C5A059]" data-testid={`corp-section-rename-input-${section.id}`} />
                      <button onClick={commitRename} className="text-[#C5A059] p-1"><Check size={14}/></button>
                      <button onClick={() => setRenamingSectionId(null)} className="text-[#5C5C5C] p-1"><X size={14}/></button>
                    </div>
                  ) : (
                    <h4 className="font-medium text-sm truncate" data-testid={`corp-section-title-${section.id}`}>{section.name}</h4>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] text-[#5C5C5C]">{section.products?.length || 0} products</span>
                  <button onClick={e => { e.stopPropagation(); startRename(section); }} className="text-[#5C5C5C] hover:text-[#C5A059] p-1" data-testid={`corp-section-rename-${section.id}`}><Pencil size={13}/></button>
                  <button onClick={e => { e.stopPropagation(); removeSection(section.id); }} className="text-[#9E2A2B] hover:bg-red-50 p-1 rounded"><X size={14}/></button>
                </div>
              </div>

              {(section.products || []).length === 0 ? (
                <p className="text-xs text-center text-[#5C5C5C] py-3 border border-dashed border-[#E5E0D8] rounded-sm">{activeSection===section.id ? 'Pick products from the right →' : 'No products'}</p>
              ) : (
                <div className="space-y-2" onClick={e => e.stopPropagation()}>
                  {section.products.map((p, pIdx) => {
                    const tiers = corporatePricing[p.id] || [];
                    return (
                      <div key={p.id} className="flex items-center gap-3 p-3 bg-[#FDFBF7] border border-[#E5E0D8] rounded-sm" data-testid={`corp-product-${p.id}`}>
                        <div className="flex flex-col">
                          <button onClick={() => moveProductInSection(section.id, pIdx, -1)} disabled={pIdx===0} className="p-0.5 text-[#C5A059] disabled:opacity-30" data-testid={`corp-product-up-${p.id}`}><ChevronUp size={12}/></button>
                          <button onClick={() => moveProductInSection(section.id, pIdx, 1)} disabled={pIdx===section.products.length-1} className="p-0.5 text-[#C5A059] disabled:opacity-30" data-testid={`corp-product-down-${p.id}`}><ChevronDown size={12}/></button>
                        </div>
                        {p.cover_image ? <img src={getImageUrl(p.cover_image)} alt="" className="w-12 h-12 object-cover rounded-sm border border-[#E5E0D8]" /> : <div className="w-12 h-12 bg-[#F5F2EB] rounded-sm" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{p.product_name}</p>
                          <p className="text-[10px] text-[#C5A059] tracking-wider">{p.product_code} · MRP {currency.symbol}{Math.round(p.mrp || 0).toLocaleString()}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {tiers.length === 0 ? (
                            <span className="text-[10px] text-[#9E2A2B] bg-red-50 border border-red-200 px-2 py-0.5 rounded-sm" data-testid={`corp-moq-missing-${p.id}`}>MOQ missing</span>
                          ) : (
                            <span className="text-[10px] text-[#2D6A4F] bg-green-50 border border-green-200 px-2 py-0.5 rounded-sm" data-testid={`corp-moq-count-${p.id}`}>{tiers.length} tier{tiers.length===1?'':'s'}</span>
                          )}
                          <Button size="sm" variant="outline" onClick={() => setMoqTarget(p)} className="rounded-sm border-[#C5A059] text-[#C5A059] hover:bg-[#F2E8D5] text-xs h-7" data-testid={`corp-moq-edit-${p.id}`}><Tag size={11} className="mr-1"/>MOQ Pricing</Button>
                        </div>
                        <button onClick={() => removeProductFromSection(section.id, p.id)} className="text-[#9E2A2B] hover:bg-red-50 p-1 rounded"><X size={13}/></button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Product picker */}
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-4 self-start sticky top-4">
          {/* Add by codes */}
          <details className="mb-3 group" data-testid="corp-add-by-codes-panel">
            <summary className="text-xs font-semibold text-[#1A1A1A] cursor-pointer flex items-center gap-1.5 select-none">
              <Plus size={12} className="text-[#C5A059]" />Add by Product Codes
            </summary>
            <div className="mt-2 space-y-2">
              <Textarea
                value={codesInput}
                onChange={e => setCodesInput(e.target.value)}
                placeholder="AR-AEM-001, AR-AEM-002&#10;AR-BTS-001"
                className="rounded-sm border-[#E5E0D8] text-xs"
                rows={3}
                data-testid="corp-codes-input"
                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleAddByCodes(); } }}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddByCodes} disabled={codesLoading || !codesInput.trim()} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm flex-1 text-xs h-8" data-testid="corp-codes-add-btn">{codesLoading ? <Loader2 size={12} className="animate-spin"/> : 'Add'}</Button>
                {codesInput && <Button size="sm" variant="outline" onClick={() => setCodesInput('')} className="rounded-sm border-[#E5E0D8] h-8"><X size={12}/></Button>}
              </div>
              <p className="text-[10px] text-[#888]">Separate by comma, newline, semicolon, or pipe. Ctrl/⌘-Enter to add.</p>
            </div>
          </details>

          {/* Filters */}
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1"><Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C5C5C]" /><Input value={filters.search} onChange={e => updateFilter('search', e.target.value)} placeholder="Search code / name / keywords…" className="pl-8 rounded-sm border-[#E5E0D8] text-sm h-9" data-testid="corp-product-search" /></div>
            <Button variant="outline" size="sm" onClick={() => setShowAdvFilters(!showAdvFilters)} className={`rounded-sm border-[#E5E0D8] h-9 ${showAdvFilters ? 'bg-[#F2E8D5]' : ''}`} data-testid="corp-toggle-adv-filters"><Filter size={14} /></Button>
            {hasActiveFilters && <Button variant="outline" size="sm" onClick={clearFilters} className="rounded-sm border-[#E5E0D8] h-9" title="Clear filters" data-testid="corp-clear-filters"><RotateCcw size={14} /></Button>}
          </div>
          {showAdvFilters && (
            <div className="grid grid-cols-2 gap-2 mb-3 p-3 bg-[#F5F2EB] rounded-sm" data-testid="corp-advanced-filters">
              <Select value={filters.brand || 'all'} onValueChange={v => updateFilter('brand', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white" data-testid="corp-filter-brand"><SelectValue placeholder="Brand" /></SelectTrigger><SelectContent><SelectItem value="all">All Brands</SelectItem>{taxonomy.brand.map(b => <SelectItem key={b.id} value={b.value}>{b.value}</SelectItem>)}</SelectContent></Select>
              <Select value={filters.category || 'all'} onValueChange={v => updateFilter('category', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white" data-testid="corp-filter-category"><SelectValue placeholder="Category" /></SelectTrigger><SelectContent><SelectItem value="all">All Categories</SelectItem>{taxonomy.category.map(c => <SelectItem key={c.id} value={c.value}>{c.value}</SelectItem>)}</SelectContent></Select>
              <Select value={filters.subcategory || 'all'} onValueChange={v => updateFilter('subcategory', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white" data-testid="corp-filter-subcategory"><SelectValue placeholder="Subcategory" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{taxonomy.subcategory.map(s => <SelectItem key={s.id} value={s.value}>{s.value}</SelectItem>)}</SelectContent></Select>
              <Select value={filters.deity || 'all'} onValueChange={v => updateFilter('deity', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white" data-testid="corp-filter-deity"><SelectValue placeholder="Deity" /></SelectTrigger><SelectContent><SelectItem value="all">All Deities</SelectItem>{taxonomy.deity.map(d => <SelectItem key={d.id} value={d.value}>{d.value}</SelectItem>)}</SelectContent></Select>
              <Select value={filters.region || 'all'} onValueChange={v => updateFilter('region', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white" data-testid="corp-filter-region"><SelectValue placeholder="Region" /></SelectTrigger><SelectContent><SelectItem value="all">All Regions</SelectItem>{taxonomy.region.map(r => <SelectItem key={r.id} value={r.value}>{r.value}</SelectItem>)}</SelectContent></Select>
              <Select value={filters.occasion || 'all'} onValueChange={v => updateFilter('occasion', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white" data-testid="corp-filter-occasion"><SelectValue placeholder="Occasion" /></SelectTrigger><SelectContent><SelectItem value="all">All Occasions</SelectItem>{taxonomy.occasion.map(o => <SelectItem key={o.id} value={o.value}>{o.value}</SelectItem>)}</SelectContent></Select>
              <Select value={filters.status || 'all'} onValueChange={v => updateFilter('status', v)}><SelectTrigger className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white" data-testid="corp-filter-status"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="discontinued">Discontinued</SelectItem></SelectContent></Select>
              <div className="grid grid-cols-2 gap-1">
                <Input type="number" placeholder="MRP min" value={filters.mrp_min} onChange={e => updateFilter('mrp_min', e.target.value)} className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white" data-testid="corp-filter-mrp-min" />
                <Input type="number" placeholder="MRP max" value={filters.mrp_max} onChange={e => updateFilter('mrp_max', e.target.value)} className="h-8 text-xs rounded-sm border-[#E5E0D8] bg-white" data-testid="corp-filter-mrp-max" />
              </div>
            </div>
          )}
          <p className="text-[10px] uppercase tracking-wider text-[#5C5C5C] mb-2" data-testid="corp-product-count">{filteredProducts.length} available</p>
          <div className="max-h-[500px] overflow-y-auto space-y-1.5">
            {filteredProducts.slice(0, 100).map(p => (
              <button key={p.id} onClick={() => addProductToSection(p)} className="w-full flex items-center gap-2 p-2 hover:bg-[#FDFBF7] rounded-sm border border-[#E5E0D8] text-left" data-testid={`corp-product-add-${p.id}`}>
                {p.cover_image ? <img src={getImageUrl(p.cover_image)} alt="" className="w-8 h-8 object-cover rounded-sm" /> : <div className="w-8 h-8 bg-[#F5F2EB] rounded-sm" />}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{p.product_name}</p>
                  <p className="text-[9px] text-[#C5A059] tracking-wider">{p.product_code}{p.mrp ? ` · ₹${Math.round(p.mrp).toLocaleString()}` : ''}</p>
                </div>
                <Plus size={12} className="text-[#C5A059]"/>
              </button>
            ))}
            {filteredProducts.length > 100 && <p className="text-[10px] text-center text-[#5C5C5C] py-2">+{filteredProducts.length-100} more — refine filters</p>}
            {filteredProducts.length === 0 && <p className="text-xs text-center text-[#5C5C5C] py-6">No products match these filters.</p>}
          </div>
        </div>
      </div>

      <MoqEditorDialog
        open={!!moqTarget}
        onClose={() => setMoqTarget(null)}
        product={moqTarget}
        mrp={moqTarget?.mrp || 0}
        currencySymbol={currency.symbol}
        tiers={moqTargetTiers}
        onSave={(tiers) => { setCorporatePricing(prev => ({ ...prev, [moqTarget.id]: tiers })); toast.success(`Saved ${tiers.length} MOQ tier${tiers.length===1?'':'s'} for ${moqTarget.product_code}`); }}
      />
    </div>
  );
}
